import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  PhoneCall,
  Search,
  Building2,
  Bot,
  Clock,
  Calendar,
  Eye,
  Play,
} from 'lucide-react';
import { getServiceCalls, getServiceCall } from '../../lib/api';
import { formatDate, formatRelativeTime, formatMinutes } from '../../lib/utils';
import { IntentSemanticsIcon, SentimentSemanticsIcon } from '../../components/call/CallSemanticsIcon';

const getNestedValue = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);

const getFirstCallValue = (call, paths = []) => {
  for (const path of paths) {
    const value = getNestedValue(call, path);
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return value;
    }
  }
  return null;
};

const getCallIntent = (call) =>
  getFirstCallValue(call, [
    'intent',
    'call.intent',
    'summary_details.intent',
    'analysis.intent',
    'ai.intent',
  ]);

const getCallCustomerSentiment = (call) =>
  getFirstCallValue(call, [
    'customerSentiment',
    'customer_sentiment',
    'call.customerSentiment',
    'call.customer_sentiment',
    'summary_details.customer_sentiment',
    'analysis.customer_sentiment',
    'customerDetails.customerSentiment',
    'customer_details.customer_sentiment',
  ]);

export default function ServiceCallsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const response = await getServiceCalls({ limit: 50 });
      setCalls(response.data.calls);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load calls');
    } finally {
      setLoading(false);
    }
  };

  const viewCallDetail = async (callId) => {
    try {
      const response = await getServiceCall(callId);
      setSelectedCall(response.data);
    } catch (error) {
      toast.error('Failed to load call details');
    }
  };

  return (
    <div className="space-y-6" data-testid="service-calls-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Call Summaries</h1>
          <p className="text-slate-500">View and analyze customer call data</p>
        </div>
      </div>

      {/* Calls List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-24" /></CardContent></Card>
          ))}
        </div>
      ) : calls.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <PhoneCall className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No calls recorded yet</h3>
            <p className="text-slate-500">
              Call data will appear here when customers start using their voice agents
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calls List */}
          <div className="lg:col-span-2 space-y-4">
            {calls.map((call) => (
              <Card 
                key={call.call_id}
                className={`cursor-pointer transition-shadow ${selectedCall?.call?.call_id === call.call_id ? 'ring-2 ring-amber-500' : 'hover:shadow-md'}`}
                onClick={() => viewCallDetail(call.call_id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <PhoneCall className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900">
                            {call.customer?.name || call.caller_number || 'Unknown Caller'}
                          </h4>
                          <Badge variant="outline">{formatMinutes(call.duration_minutes || 0)}</Badge>
                          {(getCallIntent(call) || getCallCustomerSentiment(call)) && (
                            <div
                              className="flex items-center gap-1.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IntentSemanticsIcon rawValue={getCallIntent(call)} size={15} />
                              <SentimentSemanticsIcon rawValue={getCallCustomerSentiment(call)} size={15} />
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                          {call.summary || 'No summary available'}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          {call.agent && (
                            <span className="flex items-center gap-1">
                              <Bot className="w-3 h-3" />
                              {call.agent.name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatRelativeTime(call.started_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Call Detail Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneCall className="w-5 h-5 text-green-600" />
                  Call Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedCall ? (
                  <div className="space-y-4">
                    {(getCallIntent(selectedCall) || getCallCustomerSentiment(selectedCall)) && (
                      <div className="flex flex-wrap items-center gap-3">
                        <IntentSemanticsIcon rawValue={getCallIntent(selectedCall)} size={18} />
                        <SentimentSemanticsIcon rawValue={getCallCustomerSentiment(selectedCall)} size={18} />
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-slate-500">Caller</p>
                      <p className="font-medium text-slate-900">
                        {selectedCall.customer?.name || selectedCall.call.caller_number || 'Unknown'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Duration</p>
                      <p className="font-medium text-slate-900">
                        {formatMinutes(selectedCall.call.duration_minutes || 0)}
                      </p>
                    </div>
                    {selectedCall.agent && (
                      <div>
                        <p className="text-sm text-slate-500">Agent</p>
                        <p className="font-medium text-slate-900">{selectedCall.agent.name}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-slate-500">Date</p>
                      <p className="font-medium text-slate-900">{formatDate(selectedCall.call.started_at)}</p>
                    </div>
                    {selectedCall.call.summary && (
                      <div>
                        <p className="text-sm text-slate-500">Summary</p>
                        <p className="text-slate-700 mt-1">{selectedCall.call.summary}</p>
                      </div>
                    )}
                    {selectedCall.call.transcript && (
                      <div>
                        <p className="text-sm text-slate-500">Transcript</p>
                        <div className="mt-1 p-3 bg-slate-50 rounded-lg max-h-64 overflow-y-auto">
                          <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                            {selectedCall.call.transcript}
                          </pre>
                        </div>
                      </div>
                    )}
                    {selectedCall.call.recording_url && (
                      <Button variant="outline" className="w-full" asChild>
                        <a href={selectedCall.call.recording_url} target="_blank" rel="noopener noreferrer">
                          <Play className="w-4 h-4 mr-2" />
                          Play Recording
                        </a>
                      </Button>
                    )}
                    {selectedCall.customer && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => navigate(`/service/businesses/${selectedCall.customer.user_id}`)}
                      >
                        <Building2 className="w-4 h-4 mr-2" />
                        View Business
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <PhoneCall className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Select a call to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
